import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiCalendar } from "react-icons/fi";
import ProgressMessages from "components/ProgressMessages";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import useModal from "hooks/useModal";
import useSideDrawer from "hooks/useSideDrawer";
import SideDrawer from "components/SideDrawer";
import MeetingDetails from "features/Resources/Meeting/Components/MeetingDetails";
import { useMeetingDetailQuery } from "features/Resources/Meeting/hooks";
import ScheduleMeetingForm from "features/Resources/Meeting/Components/Form/ScheduleMeetingForm";
import BulkScheduleAdjustContent from "./BulkScheduleAdjustContent";
import {
  BulkScheduleConfirmResult,
  BulkScheduleFooterState,
  BulkScheduleMeeting,
  BulkSchedulePreviewResponse,
  MeetingSelection,
} from "../models";
import {
  bulkScheduleKeys,
  useBulkScheduleConfirm,
  useBulkScheduleConfirmStatus,
  useBulkSchedulePreview,
  useSkipMeeting,
  useUpdateMeetingReservation,
} from "../hooks";
import BulkScheduleMeetingRow, { isTimeConflict, TIME_SLOT_DESCRIPTIONS } from "./BulkScheduleMeetingRow";
import ReservationCountdown from "./ReservationCountdown";

const FINDING_TIMES_MESSAGES = [
  "Finding best times for all meetings...",
  "Checking attendee calendars...",
  "Analyzing availability across time zones...",
  "Scoring meeting slots by attendee overlap...",
  "Optimizing for working hours preferences...",
  "Coordinating across multiple schedules...",
  "Almost there, finalizing recommendations...",
  "Wrapping up the last few meetings...",
];

const SCHEDULING_MESSAGES = [
  "Scheduling meetings...",
  "Creating calendar events...",
  "Sending invitations to attendees...",
  "Confirming time slots...",
  "Syncing with Google Calendar...",
  "Setting up meeting details...",
  "Almost done, finalizing the last events...",
  "Just a few more to go...",
];

interface BulkScheduleContentProps {
  journeyId: string;
  timezone: string;
  onComplete?: () => void;
  onFooterStateChange?: (state: BulkScheduleFooterState) => void;
}

interface DayGroup {
  dayKey: string;
  displayDate: string;
  dayNumber: number;
  meetings: BulkScheduleMeeting[];
}

function formatDateHeader(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    weekday: "short",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatTime(isoString: string, timezone?: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function getMeetingTime(m: BulkScheduleMeeting): number {
  const iso = m.reservation?.startTime ?? m.proposedAt;
  return iso ? new Date(iso).getTime() : Infinity;
}

function groupMeetingsByDay(meetings: BulkScheduleMeeting[], startDate: string | null): DayGroup[] {
  const activeMeetings = meetings.filter((m) => m.status !== "skipped");
  const skippedMeetings = meetings.filter((m) => m.status === "skipped");

  const map = new Map<string, BulkScheduleMeeting[]>();
  activeMeetings.forEach((m) => {
    const iso = m.reservation?.startTime ?? m.proposedAt;
    const key = iso ? new Date(iso).toDateString() : "unscheduled";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(m);
  });

  // Parse start date once for day number calculation (date-only, no time component)
  const startDay = startDate ? new Date(startDate + "T00:00:00") : null;

  const groups = Array.from(map.entries()).map(([key, dayMeetings]) => {
    const refIso = dayMeetings[0].reservation?.startTime ?? dayMeetings[0].proposedAt;

    let dayNumber: number;
    if (startDay && refIso) {
      const meetingDay = new Date(new Date(refIso).toDateString());
      dayNumber = Math.round((meetingDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    } else {
      dayNumber = 0;
    }

    // Sort meetings within each day by time
    dayMeetings.sort((a, b) => getMeetingTime(a) - getMeetingTime(b));

    return {
      dayKey: key,
      displayDate: refIso ? formatDateHeader(refIso) : "Unscheduled",
      dayNumber,
      meetings: dayMeetings,
    };
  });

  // Sort day groups chronologically
  groups.sort((a, b) => a.dayNumber - b.dayNumber);

  // Add skipped meetings as a separate section at the end
  if (skippedMeetings.length > 0) {
    groups.push({
      dayKey: "skipped",
      displayDate: "Skipped",
      dayNumber: Infinity,
      meetings: skippedMeetings,
    });
  }

  return groups;
}

function MeetingDrawerBody({ meetingId }: { meetingId: string }) {
  const { data, isPending } = useMeetingDetailQuery(meetingId);

  if (!data) return <Spinner />;

  return <MeetingDetails meeting={data} showHeader={false} isLoading={isPending} />;
}

export default function BulkScheduleContent({
  journeyId,
  timezone,
  onComplete,
  onFooterStateChange,
}: BulkScheduleContentProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: preview, isLoading, refetch, isError } = useBulkSchedulePreview(journeyId, timezone);
  const {
    isOpen: isDrawerOpen,
    onClose: onDrawerClose,
    selectedItem: drawerMeeting,
    expandClickHandlerByItem: openMeetingDrawer,
  } = useSideDrawer<BulkScheduleMeeting, string>({ getItemId: (m) => m?.meetingId?.toString() });
  const { mutate: confirmMeetings } = useBulkScheduleConfirm(journeyId);
  const { mutate: updateReservation, isPending: isUpdatingReservation } = useUpdateMeetingReservation();
  const { mutate: skipMeeting } = useSkipMeeting(journeyId);
  const [userToggles, setUserToggles] = useState<Record<number, boolean>>({});
  const [isConfirmingOptimistic, setIsConfirmingOptimistic] = useState(false);

  // While the confirm flow is active, we either have an optimistic flag (just clicked) or
  // a polling status that hasn't reached "completed" yet
  const { data: confirmStatus } = useBulkScheduleConfirmStatus(journeyId, isConfirmingOptimistic);
  const isConfirming = isConfirmingOptimistic && confirmStatus?.status !== "completed";

  const results = useMemo<BulkScheduleConfirmResult[] | null>(() => {
    if (confirmStatus?.status === "completed" && confirmStatus.results) {
      return confirmStatus.results;
    }
    return null;
  }, [confirmStatus]);
  const [adjustingMeeting, setAdjustingMeeting] = useState<BulkScheduleMeeting | null>(null);
  const [showConflictConfirm, setShowConflictConfirm] = useState(false);
  const confirmStartedAt = useRef<number | null>(null);

  const handleReservationsExpired = useCallback(() => {
    // Don't reset selections — the merge effect preserves user checkbox state
    // while the backend refreshes still-valid reservations and regenerates invalid ones
    refetch();
  }, [refetch]);

  const isRecurringGroupSession = adjustingMeeting?.meetingType === "group_session_recurring";
  const adjustModalTitle = adjustingMeeting
    ? isRecurringGroupSession
      ? `Adjust Session — ${adjustingMeeting.meetingName}`
      : `Adjust Time — ${adjustingMeeting.meetingName}`
    : "Adjust Time";

  const {
    open: openAdjustModal,
    close: closeAdjustModal,
    Modal: AdjustModal,
  } = useModal({
    headerTitle: adjustModalTitle,
    size: "2xl",
    scrollBehavior: "inside",
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Derive selections from preview data, preserving user's checkbox toggles
  const selections = useMemo<Record<number, MeetingSelection>>(() => {
    if (!preview?.meetings) return {};
    const result: Record<number, MeetingSelection> = {};
    preview.meetings.forEach((meeting) => {
      const userChoice = userToggles[meeting.meetingId];
      result[meeting.meetingId] = {
        included: userChoice !== undefined ? userChoice : meeting.reservation !== null,
        reservationId: meeting.reservation?.id ?? null,
        isAdjusted: meeting.reservation?.status === "approved",
      };
    });
    return result;
  }, [preview, userToggles]);

  const toggleableMeetings = useMemo(() => {
    if (!preview?.meetings) return [];
    return preview.meetings.filter((m) => m.status !== "skipped" && m.reservation !== null);
  }, [preview]);

  const selectedCount = useMemo(() => {
    if (!preview?.meetings) return 0;
    return preview.meetings.filter((m) => {
      if (m.status === "skipped") return false;
      return selections[m.meetingId]?.included ?? false;
    }).length;
  }, [preview, selections]);

  const allToggleableSelected =
    toggleableMeetings.length > 0 && toggleableMeetings.every((m) => selections[m.meetingId]?.included);
  const someToggleableSelected = toggleableMeetings.some((m) => selections[m.meetingId]?.included);

  const dayGroups = useMemo(
    () => (preview?.meetings ? groupMeetingsByDay(preview.meetings, preview.startDate ?? null) : []),
    [preview],
  );

  const unresolvedConflicts = useMemo(() => {
    if (!preview?.meetings) return [];
    return preview.meetings.filter((m) => {
      if (m.isRecurring) return false;
      const sel = selections[m.meetingId];
      if (!sel?.included || sel.isAdjusted || !m.reservation) return false;
      return isTimeConflict(m, m.reservation.startTime);
    });
  }, [preview, selections]);

  const handleToggle = useCallback(
    (meetingId: number) => {
      setUserToggles((prev) => {
        const meeting = preview?.meetings.find((m) => m.meetingId === meetingId);
        const previousValue = prev[meetingId];
        const currentInclusion = previousValue !== undefined ? previousValue : meeting?.reservation !== null;
        return { ...prev, [meetingId]: !currentInclusion };
      });
    },
    [preview],
  );

  const handleToggleAll = useCallback(() => {
    const newValue = !allToggleableSelected;
    setUserToggles((prev) => {
      const next = { ...prev };
      toggleableMeetings.forEach((m) => {
        next[m.meetingId] = newValue;
      });
      return next;
    });
  }, [allToggleableSelected, toggleableMeetings]);

  const handleSkip = useCallback(
    (meetingId: number) => {
      setUserToggles((prev) => ({ ...prev, [meetingId]: false }));
      skipMeeting(meetingId);
    },
    [skipMeeting],
  );

  const handleUnskip = useCallback(
    (meetingId: number) => {
      skipMeeting(meetingId);
    },
    [skipMeeting],
  );

  const handleAdjust = useCallback(
    (meetingId: number) => {
      const meeting = preview?.meetings.find((m) => m.meetingId === meetingId) ?? null;
      setAdjustingMeeting(meeting);
      openAdjustModal();
    },
    [preview, openAdjustModal],
  );

  const handleAdjustClose = useCallback(() => {
    setAdjustingMeeting(null);
    closeAdjustModal();
  }, [closeAdjustModal]);

  const handleAdjustSubmit = useCallback(
    ({
      scheduleAt,
    }: {
      scheduleAt: string;
      timezone: string;
    }) => {
      if (!adjustingMeeting) return;
      const selection = selections[adjustingMeeting.meetingId];
      if (!selection?.reservationId) return;

      const startTime = scheduleAt;
      const endTime = new Date(
        new Date(scheduleAt).getTime() + adjustingMeeting.durationMinutes * 60 * 1000,
      ).toISOString();

      updateReservation(
        { id: selection.reservationId, startTime, endTime },
        {
          onSuccess: (updatedReservation) => {
            // Update preview cache directly to avoid re-running the full preview service
            queryClient.setQueryData<BulkSchedulePreviewResponse>(bulkScheduleKeys.preview(journeyId), (old) => {
              if (!old) return old;
              return {
                ...old,
                meetings: old.meetings.map((m) => {
                  if (m.meetingId !== adjustingMeeting.meetingId) return m;
                  return {
                    ...m,
                    reservation: updatedReservation,
                  };
                }),
              };
            });
            handleAdjustClose();
          },
        },
      );
    },
    [adjustingMeeting, handleAdjustClose, selections, updateReservation, queryClient, journeyId],
  );

  const handleSessionSelect = useCallback(
    ({ calendarEventId, startTime, endTime }: { calendarEventId: string; startTime: string; endTime: string }) => {
      if (!adjustingMeeting) return;
      const selection = selections[adjustingMeeting.meetingId];
      if (!selection?.reservationId) return;

      updateReservation(
        { id: selection.reservationId, startTime, endTime, calendarEventId },
        {
          onSuccess: (updatedReservation) => {
            queryClient.setQueryData<BulkSchedulePreviewResponse>(bulkScheduleKeys.preview(journeyId), (old) => {
              if (!old) return old;
              return {
                ...old,
                meetings: old.meetings.map((m) => {
                  if (m.meetingId !== adjustingMeeting.meetingId) return m;
                  return { ...m, reservation: updatedReservation };
                }),
              };
            });
            handleAdjustClose();
          },
        },
      );
    },
    [adjustingMeeting, handleAdjustClose, selections, updateReservation, queryClient, journeyId],
  );

  const doConfirm = useCallback(() => {
    setShowConflictConfirm(false);
    const reservationIds = Object.values(selections)
      .filter((s) => s.included && s.reservationId)
      .map((s) => s.reservationId as number);

    if (reservationIds.length === 0) return;

    setIsConfirmingOptimistic(true);
    queryClient.removeQueries({ queryKey: bulkScheduleKeys.confirmStatus(journeyId) });
    confirmMeetings(reservationIds);
  }, [selections, confirmMeetings, queryClient, journeyId]);

  const handleRetryFailed = useCallback(() => {
    if (!results) return;
    const failedReservationIds = results.filter((r) => r.status === "failed").map((r) => r.reservationId);
    if (failedReservationIds.length === 0) return;

    setIsConfirmingOptimistic(true);
    queryClient.removeQueries({ queryKey: bulkScheduleKeys.confirmStatus(journeyId) });
    confirmMeetings(failedReservationIds);
  }, [results, confirmMeetings, queryClient, journeyId]);

  const handleScheduleClick = useCallback(() => {
    if (unresolvedConflicts.length > 0) {
      setShowConflictConfirm(true);
    } else {
      doConfirm();
    }
  }, [unresolvedConflicts, doConfirm]);

  const resultFailedCount = useMemo(
    () => (results ? results.filter((r) => r.status === "failed").length : 0),
    [results],
  );

  useEffect(() => {
    if (onFooterStateChange) {
      onFooterStateChange({
        isLoading,
        isConfirming,
        hasResults: results !== null,
        selectedCount,
        failedCount: resultFailedCount,
        onSchedule: handleScheduleClick,
        onRetry: resultFailedCount > 0 ? handleRetryFailed : undefined,
      });
    }
  }, [
    onFooterStateChange,
    isLoading,
    isConfirming,
    results,
    selectedCount,
    resultFailedCount,
    handleScheduleClick,
    handleRetryFailed,
  ]);

  // When polling returns completed, fire side effects once per completion.
  // Track by ref so the effect doesn't re-fire when invalidateQueries refreshes
  // confirmStatus into a new object reference.
  const completionSideEffectRef = useRef<unknown>(null);
  useEffect(() => {
    if (
      confirmStatus?.status === "completed" &&
      confirmStatus.results &&
      completionSideEffectRef.current !== confirmStatus
    ) {
      completionSideEffectRef.current = confirmStatus;
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ queryKey: bulkScheduleKeys.all });

      const scheduledCount = confirmStatus.results.filter((r) => r.status === "scheduled").length;
      const allScheduled = scheduledCount === confirmStatus.results.length;

      if (allScheduled) {
        toast({
          title: "Meetings scheduled",
          description: "All meetings have been scheduled successfully.",
          status: "success",
        });
        if (onComplete) {
          setTimeout(() => onComplete(), 1500);
        }
      } else {
        const failedCount = confirmStatus.results.length - scheduledCount;
        toast({
          title: "Some meetings failed to schedule",
          description: `${scheduledCount} of ${confirmStatus.results.length} scheduled. ${failedCount} failed — you can retry.`,
          status: "warning",
        });
      }
    }
  }, [confirmStatus, onComplete, queryClient, toast]);

  // Safety timeout: if polling hasn't resolved after 3 minutes, assume the
  // background job failed and stop the spinner so the user isn't stuck.
  useEffect(() => {
    if (isConfirming) {
      confirmStartedAt.current = Date.now();
      const timer = setTimeout(
        () => {
          setIsConfirmingOptimistic(false);
          toast({
            title: "Scheduling timed out",
            description: "The background job may have failed. Please try again.",
            status: "error",
            duration: 8000,
          });
        },
        3 * 60 * 1000,
      );
      return () => clearTimeout(timer);
    }
    confirmStartedAt.current = null;
    return undefined;
  }, [isConfirming, toast]);

  if (isLoading) {
    return <ProgressMessages messages={FINDING_TIMES_MESSAGES} />;
  }

  if (isConfirming) {
    return <ProgressMessages messages={SCHEDULING_MESSAGES} />;
  }

  if (isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        Failed to generate recommendations. Please try again.
      </Alert>
    );
  }

  if (results) {
    const scheduledCount = results.filter((r) => r.status === "scheduled").length;
    const failedCount = results.length - scheduledCount;
    const allScheduled = failedCount === 0;

    return (
      <VStack spacing={3} align="stretch">
        <Alert status={allScheduled ? "success" : "warning"}>
          <AlertIcon />
          {allScheduled
            ? "All meetings scheduled successfully."
            : `${scheduledCount} of ${results.length} meetings were scheduled. ${failedCount} meeting${failedCount !== 1 ? "s" : ""} failed to schedule.`}
        </Alert>
        {!allScheduled && !onFooterStateChange && (
          <Button colorScheme="blue" onClick={handleRetryFailed} w="full">
            Retry {failedCount} Failed Meeting{failedCount !== 1 ? "s" : ""}
          </Button>
        )}
        {onComplete && !onFooterStateChange && allScheduled && (
          <Button onClick={onComplete} w="full">
            Done
          </Button>
        )}
      </VStack>
    );
  }

  if (!dayGroups.length) {
    return (
      <VStack py={10} spacing={3} align="center">
        <Flex align="center" justify="center" w={12} h={12} borderRadius="full" bg="gray.100">
          <Box as={FiCalendar} boxSize={6} color="gray.400" />
        </Flex>
        <Text fontSize="md" color="fg.subtle" fontWeight="medium">
          No meetings to schedule
        </Text>
        <Text fontSize="sm" color="gray.500" textAlign="center" maxW="360px">
          There are no unscheduled meetings for this new hire. Add a path with meetings to their journey to get started.
        </Text>
      </VStack>
    );
  }

  return (
    <>
      <Box overflowX="auto">
        <Table variant="unstyled" size="sm">
          <Thead>
            <Tr borderBottom="1px solid" borderColor="bg.subtle">
              <Th width="52px" px={4} py={2}>
                <Tooltip
                  label={allToggleableSelected ? "Deselect all" : "Select all"}
                  placement="top"
                  hasArrow
                  isDisabled={toggleableMeetings.length === 0}
                >
                  <Box display="inline-block">
                    <Checkbox
                      isChecked={allToggleableSelected}
                      isIndeterminate={someToggleableSelected && !allToggleableSelected}
                      onChange={handleToggleAll}
                      isDisabled={isConfirming || toggleableMeetings.length === 0}
                      colorScheme="blue"
                      aria-label={allToggleableSelected ? "Deselect all meetings" : "Select all meetings"}
                    />
                  </Box>
                </Tooltip>
              </Th>
              <Th px={3} py={2} color="gray.500" fontWeight="medium" fontSize="xs" textTransform="none">
                When ({moment.tz(timezone).format("z")})
              </Th>
              <Th px={3} py={2} color="gray.500" fontWeight="medium" fontSize="xs" textTransform="none">
                Meeting Name
              </Th>
              <Th px={3} py={2} color="gray.500" fontWeight="medium" fontSize="xs" textTransform="none">
                New Hires
              </Th>
              <Th px={3} py={2} color="gray.500" fontWeight="medium" fontSize="xs" textTransform="none">
                Attendees
              </Th>
              <Th px={3} py={2} textAlign="right">
                {preview?.expiresAt ? (
                  <ReservationCountdown expiresAt={preview.expiresAt} onExpired={handleReservationsExpired} />
                ) : null}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {dayGroups.map((group) => (
              <React.Fragment key={`day-${group.dayKey}`}>
                <Tr bg="bg.surface">
                  <Td colSpan={6} px={4} py={2}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {group.dayKey === "skipped"
                          ? group.displayDate
                          : `${group.displayDate} (Day ${group.dayNumber})`}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {group.meetings.length} meeting{group.meetings.length !== 1 ? "s" : ""}
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
                {group.meetings.map((meeting) => (
                  <BulkScheduleMeetingRow
                    key={meeting.meetingId}
                    meeting={meeting}
                    selection={
                      selections[meeting.meetingId] ?? {
                        included: false,
                        reservationId: null,
                        isAdjusted: false,
                      }
                    }
                    onToggle={handleToggle}
                    onAdjust={handleAdjust}
                    onSkip={handleSkip}
                    onUnskip={handleUnskip}
                    onViewDetails={openMeetingDrawer}
                    isConfirming={isConfirming}
                  />
                ))}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>

      {!onFooterStateChange && (
        <Flex justify="flex-end" pt={4}>
          <Button
            colorScheme="blue"
            onClick={handleScheduleClick}
            isLoading={isConfirming}
            isDisabled={selectedCount === 0}
          >
            Schedule {selectedCount} Meeting{selectedCount !== 1 ? "s" : ""}
          </Button>
        </Flex>
      )}

      {/* Conflict confirmation modal */}
      <Modal isOpen={showConflictConfirm} onClose={() => setShowConflictConfirm(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm scheduling with time changes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3} color="gray.600">
              The following meetings have time conflicts:
            </Text>
            <VStack align="stretch" spacing={2}>
              {unresolvedConflicts.map((m) => (
                <Box
                  key={m.meetingId}
                  p={3}
                  bg="orange.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="orange.200"
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {m.meetingName}
                  </Text>
                  <HStack spacing={2} mt={1}>
                    {m.useSpecificTime ? (
                      <>
                        <Text fontSize="xs" color="gray.500">
                          Originally: {formatTime(m.proposedAt!, m.timezone)}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          →
                        </Text>
                        <Text fontSize="xs" color="orange.600" fontWeight="medium">
                          Scheduled: {formatTime(m.reservation!.startTime, m.timezone)}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text fontSize="xs" color="gray.500">
                          Preferred: {m.preferredTimeSlot.charAt(0).toUpperCase() + m.preferredTimeSlot.slice(1)}
                          {TIME_SLOT_DESCRIPTIONS[m.preferredTimeSlot]
                            ? ` (${TIME_SLOT_DESCRIPTIONS[m.preferredTimeSlot]})`
                            : ""}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          →
                        </Text>
                        <Text fontSize="xs" color="orange.600" fontWeight="medium">
                          Scheduled: {formatTime(m.reservation!.startTime, m.timezone)}
                        </Text>
                      </>
                    )}
                  </HStack>
                </Box>
              ))}
            </VStack>
            <Text mt={3} fontSize="sm" color="gray.500">
              Do you want to proceed with scheduling these meetings at the suggested times?
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={() => setShowConflictConfirm(false)}>
                No, go back
              </Button>
              <Button colorScheme="blue" onClick={doConfirm} isLoading={isConfirming}>
                Yes, schedule
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Adjust time modal */}
      <AdjustModal>
        {adjustingMeeting && isRecurringGroupSession ? (
          <BulkScheduleAdjustContent
            meeting={adjustingMeeting}
            isUpdatingReservation={isUpdatingReservation}
            onAdjustSubmit={handleAdjustSubmit}
            onSessionSelect={handleSessionSelect}
            onClose={handleAdjustClose}
          />
        ) : adjustingMeeting ? (
          <ScheduleMeetingForm
            meetingId={String(adjustingMeeting.meetingId)}
            onSubmit={handleAdjustSubmit}
            onComplete={handleAdjustClose}
            submitButtonText="Update Time"
            submitIsLoading={isUpdatingReservation}
            initialDate={adjustingMeeting.reservation?.startTime}
            reservationStartTime={adjustingMeeting.reservation?.startTime}
          />
        ) : null}
      </AdjustModal>

      {/* Meeting details drawer */}
      <SideDrawer<BulkScheduleMeeting>
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        item={drawerMeeting}
        header={() => <Text>{drawerMeeting?.meetingName}</Text>}
      >
        {() => (drawerMeeting ? <MeetingDrawerBody meetingId={String(drawerMeeting.meetingId)} /> : null)}
      </SideDrawer>
    </>
  );
}
